import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBell, FaArrowLeft, FaCheck, FaTrash, FaSearch } from 'react-icons/fa';
import { useNotifications } from '../hooks/useNotifications';
import { LoadingSpinner } from "../components/LoadingSpinner";
import { generateNotificationLink } from '../utils/notificationUtils';

const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  const { 
    notifications, 
    loading, 
    error, 
    unreadCount,
    markAsRead, 
    markAllAsRead, 
    deleteAll
  } = useNotifications(showOnlyUnread);

  // Filter notifications based on search and category
  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch = searchQuery === '' || 
      notification.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
      notification.entity_type.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || 
      notification.entity_type.toLowerCase().includes(categoryFilter.toLowerCase());
    
    return matchesSearch && matchesCategory;
  });

  // Get unique categories
  const categories = ['all', ...Array.from(new Set(notifications.map(n => n.entity_type.toLowerCase())))];

  const handleNotificationClick = async (notification: any) => {
    console.log('🔔 Notification clicked:', notification);
    
    if (!notification.is_read) {
      console.log('📝 Marking notification as read...');
      await markAsRead(notification.id);
    }
    
    // Generate deep link based on notification entity type and ID
    const link = generateNotificationLink(notification);
    console.log('🚀 Navigating to:', link);
    navigate(link);
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
      
      if (diffInHours < 1) return 'Just now';
      if (diffInHours < 24) return `${diffInHours}h ago`;
      if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
      
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    } catch {
      return 'Unknown time';
    }
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleDeleteAll = async () => {
    if (window.confirm('Are you sure you want to delete all notifications? This action cannot be undone.')) {
      await deleteAll();
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="bg-[#fafafa] min-h-screen">
      <header className="bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)] sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-[#aaa] hover:text-[#555] transition-colors rounded-lg"
              >
                <FaArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-[#171717]">Notifications</h1>
                <p className="text-[#555] mt-1">
                  {filteredNotifications.length === 0 
                    ? 'No notifications' 
                    : `${filteredNotifications.length} of ${notifications.length} notifications`
                  }
                  {unreadCount > 0 && (
                    <span className="ml-2 text-[#171717] font-medium">
                      • {unreadCount} unread
                    </span>
                  )}
                </p>
              </div>
            </div>
            
            {notifications.length > 0 && (
              <div className="flex items-center space-x-3">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="flex items-center space-x-2 px-4 py-2 text-sm bg-[#171717] text-white rounded-lg hover:bg-[#333] transition-colors"
                  >
                    <FaCheck className="w-3 h-3" />
                    <span>Mark All Read</span>
                  </button>
                )}
                <button
                  onClick={handleDeleteAll}
                  className="flex items-center space-x-2 px-4 py-2 text-sm bg-[#dc2626] text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <FaTrash className="w-3 h-3" />
                  <span>Delete All</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Search and Filters */}
        <div className="mb-6 bg-white rounded-xl border border-[#eaeaea] p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
              {/* Search */}
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#aaa] w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search notifications..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 w-64 text-sm border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#eaeaea] focus:border-[#eaeaea]"
                />
              </div>

              {/* Category Filter */}
              <div className="flex items-center space-x-2">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="text-sm bg-white border border-[#eaeaea] rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#eaeaea]"
                >
                  <option value="all">All Categories</option>
                  {categories.filter(c => c !== 'all').map(category => (
                    <option key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowOnlyUnread(false)}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  !showOnlyUnread 
                    ? 'bg-[#171717] text-white shadow-[0_1px_2px_rgba(0,0,0,0.04)]' 
                    : 'bg-[#f5f5f5] text-[#555] hover:bg-[#eaeaea]'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setShowOnlyUnread(true)}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  showOnlyUnread 
                    ? 'bg-[#171717] text-white shadow-[0_1px_2px_rgba(0,0,0,0.04)]' 
                    : 'bg-[#f5f5f5] text-[#555] hover:bg-[#eaeaea]'
                }`}
              >
                Unread Only
              </button>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center space-x-2">
              <FaBell className="w-5 h-5 text-red-500" />
              <p className="text-red-800 font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Notifications List */}
        {filteredNotifications.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-[#eaeaea]">
            <FaBell className="w-16 h-16 text-[#ccc] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#171717] mb-2">
              {searchQuery || categoryFilter !== 'all' ? 'No matching notifications' :
               showOnlyUnread ? 'All caught up!' : 'No notifications yet'}
            </h3>
            <p className="text-[#555]">
              {searchQuery || categoryFilter !== 'all' 
                ? 'Try adjusting your search or filters.'
                : showOnlyUnread 
                ? 'All notifications have been read!' 
                : 'When you have updates on your projects, they\'ll appear here.'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`bg-white rounded-xl border border-[#eaeaea] p-4 cursor-pointer hover:bg-[#fafafa] transition-all group ${
                  !notification.is_read 
                    ? 'bg-[#f5f5f5]/30 border-l-4 border-l-blue-500' 
                    : ''
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className={`text-sm font-medium leading-tight ${
                          !notification.is_read ? 'text-[#171717]' : 'text-[#555]'
                        }`}>
                          {notification.message}
                        </p>
                      </div>
                      
                      {!notification.is_read && (
                        <div className="w-2 h-2 bg-[#555] rounded-full flex-shrink-0 ml-3 mt-1"></div>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between mt-2">
                      <div className="text-xs text-[#aaa]">
                        <span>{formatTimestamp(notification.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications; 